import { Link, useParams } from 'react-router-dom';
import { formatDateTime } from '../../lib/api';
import { mdaName } from '../../lib/constants';
import type { Dataset } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function DatasetDetail() {
  const { code = '' } = useParams();
  const q = useApiQuery<Dataset>(`/api/v1/opengov/datasets/${encodeURIComponent(code)}`);

  return (
    <div>
      <Link to="/datasets" className="mb-4 inline-block text-sm font-semibold text-brand-600 hover:underline">
        ‹ Back to the open data catalogue
      </Link>

      {q.loading ? <Spinner /> : null}
      {q.error ? <ErrorNote message={q.error} /> : null}

      {q.data ? (
        <>
          <PageHeader
            title={q.data.title}
            subtitle={`${mdaName(q.data.ownerMda)} · dataset code ${q.data.code}`}
            actions={
              q.data.apiEnabled ? (
                <>
                  <a className="btn-secondary" href={`/api/v1/opengov/datasets/${encodeURIComponent(q.data.code)}/export.json`}>
                    Download JSON
                  </a>
                  <a className="btn-primary" href={`/api/v1/opengov/datasets/${encodeURIComponent(q.data.code)}/export.csv`}>
                    Download CSV
                  </a>
                </>
              ) : undefined
            }
          />

          {!q.data.apiEnabled ? (
            <div className="mb-6">
              <ErrorNote message="This dataset does not currently expose a live API export — files, if any, are listed below." />
            </div>
          ) : null}

          {q.data.description ? <p className="mb-6 max-w-3xl text-sm text-slate-600">{q.data.description}</p> : null}

          <div className="mb-6 grid gap-3 sm:grid-cols-3">
            <div className="card px-4 py-3.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Update frequency</div>
              <div className="mt-1.5">
                <StatusBadge status={q.data.updateFrequency} />
              </div>
            </div>
            <div className="card px-4 py-3.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Backing table</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{q.data.sourceTable.replaceAll('_', ' ')}</div>
            </div>
            <div className="card px-4 py-3.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Last updated</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{formatDateTime(q.data.updatedAt)}</div>
            </div>
          </div>

          <div className="mb-6">
            <h2 className="mb-2 text-sm font-bold text-slate-700">Field schema</h2>
            <div className="card overflow-x-auto">
              {q.data.schemaJson.fields.length === 0 ? (
                <div className="px-4 py-6 text-sm text-slate-400">No field schema published for this dataset.</div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="table-head">Field</th>
                      <th className="table-head">Type</th>
                      <th className="table-head">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.schemaJson.fields.map((f) => (
                      <tr key={f.name} className="hover:bg-slate-50">
                        <td className="table-cell font-mono text-xs font-semibold">{f.name}</td>
                        <td className="table-cell text-slate-500">{f.type}</td>
                        <td className="table-cell text-slate-500">{f.description ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div>
            <h2 className="mb-2 text-sm font-bold text-slate-700">Published files</h2>
            <div className="card overflow-x-auto">
              {q.data.files.length === 0 ? (
                <div className="px-4 py-6 text-sm text-slate-400">
                  No static files published — use the live JSON/CSV export above instead.
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="table-head">File</th>
                      <th className="table-head">Format</th>
                      <th className="table-head text-right">Size</th>
                      <th className="table-head">Uploaded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.files.map((f, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="table-cell font-medium">{f.label}</td>
                        <td className="table-cell text-slate-500">{f.format}</td>
                        <td className="table-cell text-right tabular-nums text-slate-500">{bytes(f.sizeBytes)}</td>
                        <td className="table-cell text-slate-500">{formatDateTime(f.uploadedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
